import {
  BirthdayFilledIcon,
  ChainLinkIcon,
  CheckmarkCircleFilledIcon,
  CloseCircleFilledIcon,
  LoadSpinner,
  MailFilledIcon,
  PhoneFilledIcon,
  Tooltip,
} from '@calizahq/react-components-v2'
import React from 'react'
import { useParams } from 'react-router-dom'

import { BottomSheet, FAB, Options, Tab } from '@/components'
import { TabNavigation } from '@/components/TabNavigation/TabNavigation'
import { CreateTicketForm } from '@/forms'
import { UserQueries } from '@/graphql/queries/user.queries'
import { useToggle } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'
import { MemberDetail } from '@/types/User'
import { formatDate, parseDate } from '@/util'

import { Appeasements } from './Appeasements/Appeasements'
import { viewOptionValues } from './Member.data'
import { DetailView } from './MemberDetails/DetailView'
import { MemberReservations } from './MemberReservations/MemberReservations'
import { MemberTickets } from './MemberTickets/MemberTickets'
import { StayCommitments } from './StayCommitments/StayCommitments'

export function Member() {
  const { memberId } = useParams<{ memberId: string }>()
  const { data, isLoading, refetch } = useGraphql<{ member: MemberDetail }>(
    UserQueries.QUERY_KEYS.getMember,
    UserQueries.getMember(),
    { id: Number(memberId) },
    {
      enabled: !!memberId && !!Number(memberId),
    },
  )

  const member = data?.member || undefined

  const { isShown, toggle } = useToggle()
  const { isShown: modalShown, toggle: modalToggle } = useToggle()

  const reservations = member?.reservations || []
  const reservationOptions: Options[] = reservations.map((val) => {
    return { label: val.home.searchDocument, value: val.id }
  })

  reservationOptions.unshift({ label: 'Select Reservations' })

  const ADMIN_URL = process.env.ADMIN_URL

  return (
    <div className="flex min-h-140 w-full flex-col p-4 md:p-8">
      {isLoading ? (
        <div className="flex h-20 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {member && (
            <>
              <div className="flex w-full flex-col">
                <div className="mb-4 flex w-full justify-between pt-4 md:items-center">
                  <div className="flex items-center">
                    <div className="flex mr-4">
                      <div className="flex items-center justify-between">
                        <p className="text-header-lg capitalize">{member.fullName || ''}</p>
                        {member.active ? (
                          <div title="Active Membership">
                            <CheckmarkCircleFilledIcon className="ml-2 mr-4 h-5 text-green-300" />
                          </div>
                        ) : (
                          <div title="No Active Membership">
                            <CloseCircleFilledIcon className="ml-2 mr-4 h-5 text-red-300" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col w-full mb-4 md:flex-row">
                  <div className="flex mr-8 underline">
                    <a href={`mailto: ${member.email}`} rel="noreferrer" target="_blank">
                      <div className="flex flex-row">
                        <Tooltip text="Send an email">
                          <MailFilledIcon className="w-6 mr-2" />
                        </Tooltip>
                        <div className="flex">{member.email}</div>
                      </div>
                    </a>
                  </div>
                  <div className="flex underline mr-8">
                    <a href={`dialpad://${member.phone}`} rel="noreferrer" target="_blank">
                      <div className="flex flex-row">
                        <div>
                          <Tooltip text="Call with Dialpad">
                            <PhoneFilledIcon className="w-6 mr-2" height={28} width={28} />
                          </Tooltip>
                        </div>
                        <div>{member.phone}</div>
                      </div>
                    </a>
                  </div>
                  {member.dateOfBirth && (
                    <div className="flex underline mr-8">
                      <div className="flex flex-row">
                        <div>
                          <Tooltip text="Date of Birth">
                            <BirthdayFilledIcon className="w-6 mr-2" height={28} width={28} />
                          </Tooltip>
                        </div>
                        <div>{formatDate(parseDate(member.dateOfBirth), 'MMM d, y')}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex">
                    <a href={`${ADMIN_URL}/admin/users/${member.id}`} rel="noreferrer" target="_blank">
                      <div className="flex flex-row">
                        <div>
                          <ChainLinkIcon className="w-6 mr-2" height={28} width={28} />
                        </div>
                        <div>
                          <span className="underline">View User in Admin</span>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
                <TabNavigation>
                  <Tab clickName={viewOptionValues.DETAILS} title="Details">
                    <DetailView member={member} />
                  </Tab>
                  <Tab clickName={viewOptionValues.RESERVATIONS} title="Reservations">
                    <MemberReservations member={member} />
                  </Tab>
                  <Tab clickName={viewOptionValues.TICKETS} title="Tickets">
                    <MemberTickets member={member} />
                  </Tab>
                  <Tab clickName={viewOptionValues.STAY_COMMITMENTS} title="Stay Commitments">
                    <StayCommitments member={member} />
                  </Tab>
                  <Tab clickName={viewOptionValues.APPEASEMENTS} title="Appeasements">
                    <Appeasements member={member} />
                  </Tab>
                </TabNavigation>
              </div>
              {reservationOptions.length > 0 && (
                <FAB isShown={isShown} toggle={toggle}>
                  <button
                    className="option w-full whitespace-nowrap py-3 text-left font-medium"
                    onClick={() => modalToggle()}
                  >
                    Create Ticket
                  </button>
                </FAB>
              )}
              <BottomSheet header="Create Ticket" isVisible={modalShown} onClose={modalToggle}>
                <CreateTicketForm memberId={member.id} refetchOnSuccess={refetch} toggle={modalToggle} />
              </BottomSheet>
            </>
          )}
        </>
      )}
    </div>
  )
}
